/**
 * ProductSubNav Organism Component
 * Horizontal product tab bar below the main navigation
 * Clicking a tab scrolls to the App Suite section and activates that product tab
 */

import React from 'react';
import { storyblokEditable } from '@/lib/blocks';
import { Container } from '@/components/atoms/Container';
import { Icon } from '@/components/atoms/Icon';
import { ThemeToggle } from '@/components/atoms/ThemeToggle';
import type { ProductSubNavBlok } from '@/types';

export interface ProductSubNavProps {
  blok: ProductSubNavBlok;
}

export const ProductSubNav: React.FC<ProductSubNavProps> = ({ blok }) => {
  const { label, tabs } = blok;

  const handleTabClick = (productKey: string) => {
    // Scroll to app-suite section
    const section = document.getElementById('app-suite');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
    // Notify AppSuiteSection which product to activate
    window.dispatchEvent(
      new CustomEvent('selectProduct', { detail: { productKey } })
    );
  };

  return (
    <div
      {...storyblokEditable(blok)}
      className="hidden md:block bg-theme-elevated"
    >
      <Container>
        <div className="flex items-center justify-between py-2 md:py-3">
          {/* Left: Label + Tabs */}
          <div className="flex items-center gap-4 md:gap-8 overflow-x-auto scrollbar-hide">
            {/* Label with explore icon */}
            {label && (
              <span className="flex items-center gap-1.5 text-xs font-archivo font-bold text-black dark:text-primary whitespace-nowrap mr-2 md:mr-4">
                <Icon name="explore" size="sm" className="text-black dark:text-primary" />
                {label}
              </span>
            )}

            {/* Tabs */}
            {tabs && tabs.map((tab) => (
              <button
                key={tab._uid}
                type="button"
                onClick={() => handleTabClick(tab.product_key)}
                className="px-3 py-1.5 md:px-4 md:py-2 text-xs font-archivo font-bold whitespace-nowrap transition-all duration-200 text-black hover:text-primary dark:text-primary dark:hover:text-white"
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Right: Theme Toggle */}
          <ThemeToggle />
        </div>
      </Container>
    </div>
  );
};

export default ProductSubNav;
